import type { AssetType } from '@cartwhl/common/api/getAssets';

import type { AssetLibraryTypeFilter } from './assetLibraryFilters';
import type { AssetLibraryMediaSelection } from './assetLibraryMediaSelection';
import { getMediaPickerTitleSuffix } from './assetLibraryMediaSelection';

export type AssetLibraryEmbedApiAssetType = Extract<
  AssetType,
  'MOGEN_JOB' | 'MEDIA_IMAGE' | 'MEDIA_VIDEO' | 'SCENE' | 'MASCOT_JOB'
>;

export type AssetLibraryEmbedSelection =
  | { kind: 'mogen-job'; jobId: string; label: string }
  | { kind: 'stock-motion'; motionId: string; label: string }
  | AssetLibraryMediaSelection;

export type AssetLibraryEmbedConfig = {
  /** Asset types that may appear in this embedded library context. */
  allowedAssetTypes: AssetLibraryEmbedApiAssetType[];
  /** Subset of type filters shown in the Type dropdown. */
  allowedTypeFilters: Exclude<AssetLibraryTypeFilter, 'all'>[];
  /** When true, motion clicks select instead of opening preview modals. */
  pickMode: boolean;
  /**
   * In pick mode, open scene assets directly instead of resolving and picking
   * their underlying motion (used by Motion Editor).
   */
  openSceneOnClick?: boolean;
  /** Optional suffix appended to the embedded modal library title. */
  titleSuffix?: string;
  /** When true, skip loading stock motion library items in this embed context. */
  disableMotionLibrary?: boolean;
  autoPlayThumbnails?: boolean;
  onPick?: (selection: AssetLibraryEmbedSelection) => void;
  onOpenScene?: (sceneId: string) => void;
  /**
   * When set, media assets are staged with checkmarks and confirmed via the
   * modal footer instead of picking immediately on click.
   */
  mediaSelection?: {
    mediaKind: 'image' | 'video';
    mode: 'single' | 'multi';
    maxSelection?: number;
    /** Staged checkmarks when the picker opens. */
    initialSelection?: AssetLibraryMediaSelection[];
    onConfirm: (selections: AssetLibraryMediaSelection[]) => void;
    onCancel?: () => void;
  };
};

const MOGEN_MOTION_PICKER_TYPE_FILTERS = [
  'scene',
  'canvas-project',
  'comic-batch',
  'swing-batch',
] as const satisfies ReadonlyArray<Exclude<AssetLibraryTypeFilter, 'all'>>;

export function createMogenMotionPickerEmbedConfig(
  onPick: (selection: AssetLibraryEmbedSelection) => void,
): AssetLibraryEmbedConfig {
  return {
    allowedAssetTypes: ['SCENE', 'MOGEN_JOB'],
    allowedTypeFilters: [...MOGEN_MOTION_PICKER_TYPE_FILTERS],
    pickMode: true,
    titleSuffix: ' (motions)',
    autoPlayThumbnails: true,
    onPick,
  };
}

export function createMotionEditorAssetLibraryEmbedConfig(input: {
  onPick: (selection: AssetLibraryEmbedSelection) => void;
  onOpenScene: (sceneId: string) => void;
}): AssetLibraryEmbedConfig {
  return {
    ...createMogenMotionPickerEmbedConfig(input.onPick),
    openSceneOnClick: true,
    onOpenScene: input.onOpenScene,
  };
}

export function createMediaImagePickerEmbedConfig(input: {
  mode: 'single' | 'multi';
  maxSelection?: number;
  initialSelection?: AssetLibraryMediaSelection[];
  onConfirm: (selections: AssetLibraryMediaSelection[]) => void;
  onCancel?: () => void;
}): AssetLibraryEmbedConfig {
  return {
    allowedAssetTypes: ['MEDIA_IMAGE'],
    allowedTypeFilters: ['image'],
    pickMode: true,
    titleSuffix: getMediaPickerTitleSuffix('image'),
    disableMotionLibrary: true,
    autoPlayThumbnails: false,
    mediaSelection: {
      mediaKind: 'image',
      mode: input.mode,
      maxSelection: input.maxSelection,
      initialSelection: input.initialSelection,
      onConfirm: input.onConfirm,
      onCancel: input.onCancel,
    },
  };
}

export function createMediaVideoPickerEmbedConfig(input: {
  mode: 'single' | 'multi';
  maxSelection?: number;
  initialSelection?: AssetLibraryMediaSelection[];
  onConfirm: (selections: AssetLibraryMediaSelection[]) => void;
  onCancel?: () => void;
}): AssetLibraryEmbedConfig {
  return {
    allowedAssetTypes: ['MEDIA_VIDEO'],
    allowedTypeFilters: ['video'],
    pickMode: true,
    titleSuffix: getMediaPickerTitleSuffix('video'),
    disableMotionLibrary: true,
    autoPlayThumbnails: false,
    mediaSelection: {
      mediaKind: 'video',
      mode: input.mode,
      maxSelection: input.maxSelection,
      initialSelection: input.initialSelection,
      onConfirm: input.onConfirm,
      onCancel: input.onCancel,
    },
  };
}
